// Pure helper that derives the corner-ticker payload from raw `unit:combat`
// events + locality info. The React component renders this directly; keep
// the helper dependency-free so it can be exercised under `bun test`
// without pulling React or workspace packages.
//
// Spec: docs/02-systems/combat-visualisation.md §1a, §1b.

import { type CombatAxialCoord, shouldAnimateCombat } from './shouldAnimateCombat.js';
import type { SimEvent } from './useCombatEngagements.js';

export interface CombatDrawerNearFight {
  readonly attackerId: string;
  readonly defenderId: string;
  readonly position: CombatAxialCoord;
}

export interface CombatDrawerSummary {
  /** Total `unit:combat` events in the lookback window. Drives the "N active" badge. */
  readonly activeCount: number;
  /** Distinct near (animateable) fights, deduped by attacker/defender pair. */
  readonly nearFights: ReadonlyArray<CombatDrawerNearFight>;
  /** Number of fights that fall outside the near radius — drives the rollup line. */
  readonly distantCount: number;
}

function readCoord(payload: Record<string, unknown>, key: string): CombatAxialCoord | null {
  const v = payload[key] as { q?: unknown; r?: unknown } | undefined;
  if (!v) return null;
  if (typeof v.q !== 'number' || typeof v.r !== 'number') return null;
  if (!Number.isFinite(v.q) || !Number.isFinite(v.r)) return null;
  return { q: v.q, r: v.r };
}

export function summarizeCombatDrawer(
  events: ReadonlyArray<SimEvent>,
  localPlayerIndex: number,
  localCapital: CombatAxialCoord | null,
): CombatDrawerSummary {
  const nearFights: CombatDrawerNearFight[] = [];
  const seenPairs = new Set<string>();
  let activeCount = 0;
  let distantCount = 0;

  for (const ev of events) {
    if (ev.type !== 'unit:combat') continue;
    const payload = ev.payload;
    if (!payload) continue;
    const attackerId = typeof payload.unitId === 'string' ? (payload.unitId as string) : null;
    const defenderId =
      typeof payload.defenderId === 'string' ? (payload.defenderId as string) : null;
    const playerIndex =
      typeof payload.playerIndex === 'number' ? (payload.playerIndex as number) : null;
    const to = readCoord(payload, 'to');
    if (!attackerId || !defenderId || playerIndex === null || !to) continue;

    activeCount++;

    const animate = shouldAnimateCombat(
      { attackerOwnerId: playerIndex, defenderOwnerId: null, position: to },
      localPlayerIndex,
      localCapital,
    );

    if (!animate) {
      distantCount++;
      continue;
    }

    const pairKey = `${attackerId}::${defenderId}`;
    if (seenPairs.has(pairKey)) continue;
    seenPairs.add(pairKey);
    nearFights.push({ attackerId, defenderId, position: to });
  }

  return { activeCount, nearFights, distantCount };
}

/** Formatted rollup line for the corner ticker when `distantCount > 0`. */
export function formatDistantSummary(distantCount: number): string | null {
  if (distantCount <= 0) return null;
  return `Continental Pact — ${distantCount} settlement${distantCount === 1 ? '' : 's'} lost`;
}

/**
 * Best-effort region label for a fight coordinate. "capital" when the fight
 * sits exactly on the local capital; otherwise a coarse compass direction
 * derived from the axial offset (the spec defers prettier region naming
 * to v1.1).
 */
export function labelRegion(position: CombatAxialCoord, capital: CombatAxialCoord | null): string {
  if (capital && position.q === capital.q && position.r === capital.r) {
    return 'capital';
  }
  const dq = capital ? position.q - capital.q : position.q;
  const dr = capital ? position.r - capital.r : position.r;
  if (Math.abs(dq) >= Math.abs(dr) * 2) {
    return dq > 0 ? 'east' : 'west';
  }
  if (Math.abs(dr) >= Math.abs(dq) * 2) {
    return dr > 0 ? 'south' : 'north';
  }
  // Diagonals — pick the dominant axis.
  if (Math.abs(dq) >= Math.abs(dr)) {
    return dq > 0 ? 'east' : 'west';
  }
  return dr > 0 ? 'south' : 'north';
}
